// React imports
import React, { useState, useCallback, useEffect } from 'react'

// Material-UI components
import { Drawer, Box, Typography, Divider } from '@material-ui/core'

// Material-UI styles
import { makeStyles } from '@material-ui/core/styles'

// Atlas components and styles
import { AtlasIcon } from '@diligentcorp/atlas-react'
import {
    AtlasWhite_100,
    ColorBaseMulberryBlue,
    ColorBaseWhite,
    ColorEastBay
} from 'assets/styles/variables'

// Constants and utilities
import i18n from 'common/util/internationalization'
import { addKeyPressHandler } from 'common/util/functions'
import fileSaver from 'file-saver'

// Icons
import { IconButton as StyledIconButton } from 'components/shared/StyledComponents'

// Custom components
import { AddActionEditActionButton } from 'components/shared/StyledComponents'
import { AtlasButton } from 'components/atlas-material/button'
import ExportOptions, { OptionType } from 'components/shared/ExportOptions'
import SelectedTemplateCard from './SelectedTemplateCard'
import LinearIndeterminate from 'components/shared/LinearProgress'
import { CircularProgressLoader } from 'components/global-progress-modal/global-progress-modal'

// APIs
import {
    fetchUserPreferredTemplate,
    setUserPreferredTemplate,
    generateDocxFromTemplate,
    fetchPreviewTemplatePDF
} from 'businesslayer/networkrequests/templateService'
import { useMinutesTemplates } from 'businesslayer/networkrequests/templates/useMinutesTemplates'
import TemplatePreviewHybrid from './TemplatePreviewHybrid'
import { getTemplateServiceToken } from 'businesslayer/networkrequests'
import { getLocalStorageItem, getSessionStorageItem } from 'businesslayer/minutesLocalStore'
import ssoConfig from '../../config/ssoConfig.json'
import moment from 'moment-timezone'

const DRAWER_WIDTH = 658
const DEFAULT_SORT_BY = 'updatedAt'
const DEFAULT_SORT_ORDER = 'desc'
const ACTIONS_ONLY = 'actionsOnly'
const MINUTES_ONLY = 'minutesOnly'

const useStyles = makeStyles((theme) => ({
    drawer: {
        width: DRAWER_WIDTH,
        [theme.breakpoints.down('md')]: {
            width: '100vw'
        }
    },
    drawerPaper: {
        position: 'fixed',
        width: DRAWER_WIDTH,
        [theme.breakpoints.down('md')]: {
            width: '100vw'
        }
    },
    headerTitle: {
        fontWeight: 600,
        fontSize: '22px',
        padding: '16px',
        lineHeight: '26px',
        fontFamily: 'Source Sans Pro'
    },
    exportOptionsSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '40px',
        alignSelf: 'stretch',
        overflowY: 'auto',
        flex: 1,
        '&::-webkit-scrollbar': {
            width: '8px'
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
            '&:hover': {
                background: '#555'
            }
        }
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '40px',
        alignSelf: 'stretch'
    },
    root: {
        padding: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        gap: '16px'
    },
    rootContainer: {
        borderRadius: 12,
        backgroundColor: AtlasWhite_100
    },
    loaderContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        flex: 1,
        minHeight: 'calc(100vh - 144px)'
    },

    footer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '16px',
        alignSelf: 'stretch',
        height: '72px',
        backgroundColor: '#fff',
        zIndex: theme.zIndex.drawer + 1,
        flexShrink: 0
    },
    footerComp: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        padding: theme.spacing(2),
        gap: '16px',
        position: 'absolute',
        right: 0
    },
    saveTemplate: {
        textTransform: 'none',
        backgroundColor: ColorEastBay,
        display: 'flex',
        color: '#fff',
        borderWidth: '0px',
        borderRadius: '3px',
        letterSpacing: '0.5px',
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        padding: '8px 12px',
        fontWeight: 600,
        placeContent: 'center',
        gap: '8px',
        minWidth: '78px',
        '&:focus, &:active': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px ${ColorBaseWhite}, 0px 0px 0px 3px ${ColorBaseMulberryBlue}`,
            borderRadius: '2px',
            backgroundColor: `${ColorEastBay}`
        },
        '&:hover': {
            backgroundColor: '#364262',
            color: '#fff',
            cursor: 'pointer'
        }
    },

    headerBox: {
        height: '72px',
        backgroundColor: '#fff',
        zIndex: theme.zIndex.drawer + 1,
        flexShrink: 0,
        display: 'flex'
    },
    btnContainer: {
        float: 'right',
        display: 'flex',
        alignItems: 'center',
        margin: 'auto',
        marginRight: '16px',
        textDecoration: 'none'
    },
    closeButton: {
        '&:focus, &:active': {
            padding: '2px',
            margin: '2px',
            outline: `2px solid ${ColorBaseMulberryBlue}`
        }
    }
}))

const ExportDrawer = ({
    isOpen,
    setIsOpen,
    onExportActionsOnly,
    minutesItem,
    setToastMessage,
    setShowToast,
    setToastType
}) => {
    // Constants and state
    const isAdmin = true
    const classes = useStyles({ isAdmin })

    const { templateLists, isLoading, fetchMinutesTemplates, totalTemplates, systemTemplate } =
        useMinutesTemplates()
    const [exportOption, setExportOption] = useState('')
    const [templateOption, setTemplateOption] = useState<any>({})
    const [previewSelection, setPreviewSelection] = useState<any>({})
    const [shouldShowTemplateList, setShouldShowTemplateList] = useState(false)

    // Load more state
    const [page, setPage] = useState(1)
    const [perPage] = useState(20) // Fixed per page for now
    const [hasMoreTemplates, setHasMoreTemplates] = useState(false)

    // Combined templates state - diligent template first, then regular templates
    const [combinedTemplates, setCombinedTemplates] = useState<any[]>([])
    const [userPreferredTemplateResponse, setUserPreferredTemplateResponse] = useState<any>(null)
    const [isLoadingUserPreferred, setIsLoadingUserPreferred] = useState(false)

    const isBoardLogin = () => {
        const platform = getSessionStorageItem('parentPlatform')
        return platform === 'boardswebadmin' || platform === 'boardswebdirector'
    }
    // Initialize token auto-renewal system for template operations
    useEffect(() => {
        const { _env_ } = window
        const SSO_AUTHORITY_URL =
            process.env.NODE_ENV !== 'development' ? _env_.SSO_AUTHORITY_URL : ssoConfig.authority
        // Get current access token from localStorage
        const currentAccessToken = getLocalStorageItem('sessionId')
        if (isBoardLogin() || process.env.NODE_ENV === 'development') {
            if (currentAccessToken) {
                // Create params for token renewal
                const params = new URLSearchParams()
                params.append('client_id', ssoConfig.minutes_clientId)
                params.append('grant_type', ssoConfig.template_doc_service_grantType)
                params.append('token', currentAccessToken)
                params.append('scope', ssoConfig.template_doc_service_scope)

                // Initialize auto-renewal for any existing template token
                getTemplateServiceToken(SSO_AUTHORITY_URL, params)
            } else {
                console.error('No access token available for template service auto-renewal')
            }
        }
    }, [])
    // Fetch templates on initial load
    useEffect(() => {
        if (page === 1) {
            fetchMinutesTemplates(page, DEFAULT_SORT_BY, DEFAULT_SORT_ORDER, perPage)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [perPage, page])

    // Update combined templates when systemTemplate or templateLists change
    useEffect(() => {
        const systemTemplatesArray = Array.isArray(systemTemplate)
            ? systemTemplate.filter(Boolean)
            : systemTemplate
            ? [systemTemplate]
            : []
        const listTemplatesArray = Array.isArray(templateLists) ? templateLists : []
        const combined = [...systemTemplatesArray, ...listTemplatesArray]

        setCombinedTemplates(combined)
    }, [systemTemplate, templateLists])

    // Update hasMoreTemplates when templateLists, totalTemplates, or drawer state changes
    useEffect(() => {
        if (templateLists !== undefined) {
            const loadedCount = templateLists?.length || 0
            const hasMore = totalTemplates > 0 && loadedCount < totalTemplates
            setHasMoreTemplates(hasMore)
        }
    }, [templateLists, totalTemplates, isOpen])

    // Fetch user preferred template on component mount
    useEffect(() => {
        if (isOpen) {
            const getUserPreferredTemplate = async () => {
                try {
                    setIsLoadingUserPreferred(true)
                    const response = await fetchUserPreferredTemplate()
                    setUserPreferredTemplateResponse(response)
                } catch (error) {
                    console.error('Failed to fetch user preferred template:', error)
                    setUserPreferredTemplateResponse(null)
                } finally {
                    setIsLoadingUserPreferred(false)
                }
            }
            getUserPreferredTemplate()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    // Process user preferred template once combinedTemplates are available
    useEffect(() => {
        if (userPreferredTemplateResponse && combinedTemplates.length > 0) {
            const response = userPreferredTemplateResponse
            if (response?.preferredTemplate || response?.preferredExportOption) {
                const preferredTemplate = combinedTemplates.find(
                    (template) => template.id === response.preferredTemplate
                )
                // If no preferred template found, try to find default template, otherwise use first template
                const fallbackTemplate =
                    preferredTemplate ||
                    combinedTemplates.find((template) => template.isDefault === true) ||
                    combinedTemplates[0]
                setTemplateOption(fallbackTemplate)
                setExportOption(response?.preferredExportOption)
                if (response?.preferredExportOption === ACTIONS_ONLY) {
                    setShouldShowTemplateList(false)
                }
            } else {
                setTemplateOption({})
                setExportOption('')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPreferredTemplateResponse, combinedTemplates])

    // When templates are loaded and templateOption is still empty/null, select the default template or first template
    useEffect(() => {
        if (
            !exportOption &&
            combinedTemplates &&
            combinedTemplates.length > 0 &&
            templateOption &&
            Object.keys(templateOption)?.length === 0
        ) {
            // Try to find default template, otherwise use first template
            const defaultTemplate =
                combinedTemplates.find((template) => template.isDefault === true) ||
                combinedTemplates[0]
            setTemplateOption(defaultTemplate)
            setExportOption(MINUTES_ONLY)
            setShouldShowTemplateList(true)
        } else if (
            exportOption !== ACTIONS_ONLY &&
            combinedTemplates &&
            combinedTemplates.length > 0
        ) {
            setShouldShowTemplateList(true)
            if (templateOption && Object.keys(templateOption)?.length === 0) {
                // Try to find default template, otherwise use first template
                const defaultTemplate =
                    combinedTemplates.find((template) => template.isDefault === true) ||
                    combinedTemplates[0]
                setTemplateOption(defaultTemplate)
            }
        }
    }, [exportOption, combinedTemplates, templateOption])

    const isExportButtonEnabled = () => {
        if (shouldShowTemplateList) {
            return templateOption
        } else {
            return exportOption === ACTIONS_ONLY
        }
    }

    const getExportAnalyticsValue = () => {
        const selectedTemplateId = templateOption?.id
        const firstSystemTemplateId = Array.isArray(systemTemplate)
            ? systemTemplate[0]?.id
            : undefined
        const secondSystemTemplateId = Array.isArray(systemTemplate)
            ? systemTemplate[1]?.id
            : undefined

        const analyticsByExportOptionType: Record<
            string,
            {
                BoardsMeetingTemplate: string
                LegacyDiligentTemplate: string
                CustomTemplate: string
            }
        > = {
            minutesOnly: {
                BoardsMeetingTemplate: 'MM-Template-ExportMinutesOnly-BoardsMeetingTemplate',
                LegacyDiligentTemplate: 'MM-Template-ExportMinutesOnly-LegacyDiligentTemplate',
                CustomTemplate: 'MM-Template-ExportMinutesOnly-CustomTemplate'
            },
            minutesWithActions: {
                BoardsMeetingTemplate: 'MM-Template-ExportMinutesWithActions-BoardsMeetingTemplate',
                LegacyDiligentTemplate:
                    'MM-Template-ExportMinutesWithActions-LegacyDiligentTemplate',
                CustomTemplate: 'MM-Template-ExportMinutesWithActions-CustomTemplate'
            }
        }

        switch (exportOption) {
            case 'actionsOnly':
                return 'MM-Template-ExportActionsOnly'
            case 'minutesOnly': {
                const map = analyticsByExportOptionType.minutesOnly
                if (!selectedTemplateId) return map.CustomTemplate
                if (firstSystemTemplateId && selectedTemplateId === firstSystemTemplateId)
                    return map.BoardsMeetingTemplate
                if (secondSystemTemplateId && selectedTemplateId === secondSystemTemplateId)
                    return map.LegacyDiligentTemplate
                return map.CustomTemplate
            }
            case 'minutesWithActions': {
                const map = analyticsByExportOptionType.minutesWithActions
                if (!selectedTemplateId) return map.CustomTemplate
                if (firstSystemTemplateId && selectedTemplateId === firstSystemTemplateId)
                    return map.BoardsMeetingTemplate
                if (secondSystemTemplateId && selectedTemplateId === secondSystemTemplateId)
                    return map.LegacyDiligentTemplate
                return map.CustomTemplate
            }
            default:
                return 'MM-Template-Export'
        }
    }

    const toggleDrawer = useCallback(() => {
        setIsOpen(!isOpen)
        setExportOption('')
        setTemplateOption({})
        setUserPreferredTemplateResponse(null)
        setPage(1) // Reset to first page when drawer is toggled
        setHasMoreTemplates(false) // Reset has more state
    }, [setIsOpen, isOpen])

    // Handle load more templates
    const handleLoadMore = useCallback(async () => {
        const nextPage = page + 1
        setPage(nextPage)
        await fetchMinutesTemplates(nextPage, DEFAULT_SORT_BY, DEFAULT_SORT_ORDER, perPage)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage])

    const handleExport = async () => {
        setIsExporting(true)
        const templateId = templateOption?.id || null

        try {
            // Handle actions-only export
            if (exportOption === ACTIONS_ONLY) {
                onExportActionsOnly?.()
                await setUserPreferredTemplate(templateId, exportOption)
                toggleDrawer()
                return
            }

            // Save user's preferred template with current exportOption and templateId
            try {
                await setUserPreferredTemplate(templateId, exportOption)
            } catch (error) {
                console.error('Failed to update preferred template:', error)
            }

            // Generate DOCX from template
            // Get required values for the new API
            const minutesDocumentId = minutesItem?.id
            const committeeId = window.sessionStorage.getItem('currentCommitteeId')
            const apiToken =
                window.localStorage.getItem('sessionId') || getSessionStorageItem('sessionId')
            const withActions = exportOption === 'minutesWithActions'
            const timezone = encodeURIComponent(moment.tz.guess())
            if (!minutesDocumentId || !committeeId || !apiToken || !timezone) {
                throw new Error('Missing required export parameters')
            }
            const result = await generateDocxFromTemplate({
                templateId,
                minutesDocumentId: Number(minutesDocumentId),
                committeeId: Number(committeeId),
                withActions,
                apiToken,
                timezone
            })
            if (result.success && result.response) {
                const blob = await result.response.blob()
                fileSaver.saveAs(blob, 'MinutesExport.docx')
                setShowToast(true)
                setToastType('success')
                setToastMessage(i18n.t('TEMPLATE_EXPORT_SUCCESS'))
            } else {
                setShowToast(true)
                setToastType('error')
                setToastMessage(i18n.t('TEMPLATE_EXPORT_FAIL'))
                console.error('Failed to generate DOCX:', result.error)
            }
        } catch (error) {
            setShowToast(true)
            setToastType('error')
            setToastMessage(i18n.t('TEMPLATE_EXPORT_FAIL'))
            console.error('Error during DOCX generation:', error)
        } finally {
            setIsExporting(false)
            toggleDrawer()
        }
    }

    const [previewOpen, setPreviewOpen] = useState(false)
    const [imageUrls, setImageUrls] = useState<string[]>([])
    const [loading, setLoading] = useState(false)
    const [isExporting, setIsExporting] = useState(false)

    const handleGetTemplatePreview = useCallback(
        async (templateId: string) => {
            setPreviewOpen(true)
            const selectedTemplate = combinedTemplates.find(
                (template) => template?.id === templateId
            )
            if (selectedTemplate) {
                setPreviewSelection(selectedTemplate)
            }
            try {
                setLoading(true)
                const response = await fetchPreviewTemplatePDF(templateId)
                if (response?.success && response?.images && response?.images?.length > 0) {
                    setImageUrls(response?.images)
                } else {
                    setImageUrls([])
                    console.error('Error fetching template preview:', response?.error)
                }
            } catch (err) {
                setImageUrls([])
                console.error('Error fetching template preview:', err)
            } finally {
                setLoading(false)
            }
        },
        [combinedTemplates, setPreviewOpen, setLoading, setImageUrls]
    )

    const handleClosePreview = useCallback(() => {
        setPreviewOpen(false)
    }, [setPreviewOpen])

    return (
        <>
            <Drawer
                className={classes.drawer}
                classes={{ paper: classes.drawerPaper }}
                anchor="right"
                open={isOpen}
                onClose={toggleDrawer}
            >
                <Box className={classes.headerBox}>
                    <Typography className={classes.headerTitle}>
                        {i18n.t('EXPORT_OPTIONS')}
                    </Typography>
                    <Box className={classes.btnContainer}>
                        <StyledIconButton
                            disableFocusRipple
                            disableRipple
                            onClick={() => toggleDrawer()}
                            data-analytics="MM-Template-Export-Close"
                        >
                            <div
                                className={classes.closeButton}
                                role="button"
                                aria-label="Close"
                                tabIndex={0}
                                onClick={() => toggleDrawer()}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault()
                                        toggleDrawer()
                                    }
                                }}
                            >
                                <AtlasIcon data-testid="AtlasModal_CloseIcon" name="close" />
                            </div>
                        </StyledIconButton>
                    </Box>
                </Box>
                <LinearIndeterminate loading={isExporting} />

                <Box className={classes.exportOptionsSection}>
                    {isLoadingUserPreferred ? (
                        <Box className={classes.loaderContainer}>
                            <CircularProgressLoader />
                        </Box>
                    ) : (
                        <Box className={classes.section}>
                            <ExportOptions
                                exportOption={exportOption as OptionType}
                                setExportOption={setExportOption}
                                setShouldShowTemplateList={setShouldShowTemplateList}
                            />

                            {combinedTemplates?.length && combinedTemplates?.length > 0 ? (
                                <SelectedTemplateCard
                                    handleGetTemplatePreview={handleGetTemplatePreview}
                                    templateOption={templateOption}
                                    templateOptions={combinedTemplates}
                                    shouldShowTemplateList={shouldShowTemplateList}
                                />
                            ) : (
                                !isLoading
                            )}
                        </Box>
                    )}
                </Box>

                <Divider />
                <Box className={classes.footer}>
                    <Box className={classes.footerComp}>
                        <AddActionEditActionButton
                            key="actionCancel"
                            disableFocusRipple
                            disableRipple
                            disabled={false}
                            onClick={toggleDrawer}
                            data-analytics="MM-Template-Export-Cancel"
                        >
                            {i18n.t('CANCEL')}
                        </AddActionEditActionButton>
                        <AtlasButton
                            role="button"
                            onKeyPress={(e) => (addKeyPressHandler(e) ? handleExport() : null)}
                            disableRipple
                            className={classes.saveTemplate}
                            key="actionSave"
                            disabled={
                                !isExportButtonEnabled() || isExporting || isLoadingUserPreferred
                            }
                            type="submit"
                            onClick={handleExport}
                            disableFocusRipple
                            data-analytics={getExportAnalyticsValue()}
                        >
                            {i18n.t('EXPORT')}
                        </AtlasButton>
                    </Box>
                </Box>
            </Drawer>

            {/* Preview Hybrid (Modal for Desktop, Drawer for iPad) */}
            <TemplatePreviewHybrid
                open={previewOpen}
                onClose={handleClosePreview}
                imageUrlsData={imageUrls}
                loading={loading}
                onCancel={handleClosePreview}
                templateOption={templateOption}
                setTemplateOption={setTemplateOption}
                systemTemplate={systemTemplate}
                templateOptions={templateLists}
                hasMoreTemplates={hasMoreTemplates}
                handleLoadMore={handleLoadMore}
                isLoading={isLoading}
                handleGetTemplatePreview={handleGetTemplatePreview}
                previewSelection={previewSelection}
                setPreviewSelection={setPreviewSelection}
                userPreferredTemplateResponse={userPreferredTemplateResponse}
            />
        </>
    )
}

export default ExportDrawer
